import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InquiryModal from './InquiryModal';

const FloatingCTA = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isVisible, setIsVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    
    // Modal state for inquiries
    const [modalData, setModalData] = useState({ isOpen: false, type: 'ESTIMATE' });
    
    const menuRef = useRef(null);

    // Only show on home, solutions, products or content pages after a short scroll
    useEffect(() => {
        const toggleVisibility = () => {
            if (window.scrollY > 200) {
                setIsVisible(true);
            } else {
                setIsVisible(false);
                setIsExpanded(false); // Close menu if scrolling up
            }
        };

        window.addEventListener('scroll', toggleVisibility);
        return () => window.removeEventListener('scroll', toggleVisibility);
    }, []);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };
        
        if (isExpanded) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isExpanded]);

    // Hide entirely on admin routes
    if (location.pathname.startsWith('/admin')) {
        return null;
    }

    const handleActionClick = (type, path) => {
        setIsExpanded(false);
        if (path) {
            navigate(path);
        } else {
            setModalData({ isOpen: true, type });
        }
    };

    // Sub-actions definition
    const actions = [
        { id: 'DEMO', icon: '💻', label: '데모 신청', type: 'DEMO' },
        { id: 'PROPOSAL', icon: '📋', label: '표준 제안서', type: 'PROPOSAL' },
        { id: 'ESTIMATE_REQ', icon: '📐', label: '맞춤 견적 요청', type: 'ESTIMATE' },
        { id: 'CONTACT', icon: '☕', label: '상담/미팅', type: 'CONTACT' },
        { id: 'ESTIMATE_SELF', icon: '🚀', label: '3분 견적', path: '/estimate' } // Uses Route
    ];

    return (
        <>
            <InquiryModal
                isOpen={modalData.isOpen}
                onClose={() => setModalData(prev => ({ ...prev, isOpen: false }))}
                type={modalData.type}
            />

            <div 
                ref={menuRef}
                className={`fixed bottom-8 right-8 z-40 transition-all duration-500 transform ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0 pointer-events-none'
                }`}
            >
                {/* Expandable Menu Items */}
                <div className={`absolute bottom-full right-0 mb-4 flex flex-col items-end gap-3 transition-all duration-300 origin-bottom ${
                    isExpanded ? 'scale-100 opacity-100' : 'scale-50 opacity-0 pointer-events-none'
                }`}>
                    {actions.map((action, index) => (
                        <button
                            key={action.id}
                            onClick={() => handleActionClick(action.type, action.path)}
                            className="group flex items-center justify-end gap-3 transform transition-all duration-200 hover:-translate-x-1"
                            style={{ transitionDelay: `${isExpanded ? (actions.length - index) * 50 : 0}ms` }}
                        >
                            {/* Label pill - always visible when expanded for B2B clarity */}
                            <span className={`text-sm font-bold py-2 px-4 rounded-xl shadow-lg border transition-colors whitespace-nowrap ${
                                action.id === 'ESTIMATE_SELF' 
                                ? 'bg-primary-500 text-white border-primary-500 shadow-glow' 
                                : 'bg-white text-gray-800 border-gray-100 group-hover:text-primary-500 group-hover:border-primary-200'
                            }`}>
                                {action.label}
                            </span>
                            {/* Icon button */}
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg border text-xl group-hover:scale-110 transition-all ${
                                action.id === 'ESTIMATE_SELF'
                                ? 'bg-white text-primary-500 border-primary-200 ring-2 ring-primary-100'
                                : 'bg-white border-gray-100 group-hover:bg-primary-50'
                            }`}>
                                {action.icon}
                            </div>
                        </button>
                    ))}
                </div>

                {/* Main Action Button */}
                <div className="relative group flex justify-end">
                    <div className={`absolute -inset-1 rounded-full blur transition duration-300 ${isExpanded ? 'opacity-0' : 'bg-gradient-to-r from-primary-400 to-primary-600 opacity-40 group-hover:opacity-100'}`}></div>
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className={`relative flex items-center justify-center text-white rounded-full shadow-xl transition-all duration-300 ${isExpanded ? 'w-14 h-14 bg-gray-500 hover:bg-gray-600 hover:scale-105' : 'w-[72px] h-[72px] bg-primary-500 hover:bg-primary-600 hover:scale-105 p-2'}`}
                        aria-label={isExpanded ? "문의 메뉴 닫기" : "문의 메뉴 열기"}
                    >
                        {isExpanded ? (
                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <path d="M18 6L6 18M6 6l12 12"></path>
                            </svg>
                        ) : (
                            <div className="flex flex-col items-center justify-center">
                                <span className="text-2xl mb-0.5">🚀</span>
                                <span className="text-[10px] font-extrabold tracking-tighter whitespace-nowrap leading-tight">3분 셀프견적</span>
                            </div>
                        )}
                        
                        {/* Notification dot (only show when collapsed) */}
                        {!isExpanded && (
                            <span className="absolute top-0 right-0 flex h-4 w-4">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-4 w-4 bg-yellow-500 border-2 border-white"></span>
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

export default FloatingCTA;
